import { createAction, type QueuedAction } from "@/v2/hooks/useActionQueue";
import { type ActionDeps } from "./types";

export type AddPayload = {
	id: number;
	date: string;
	matter: string;
	matter_id: string;
	description: string;
	duration: number;
	task: string;
	activity: string;
	end_time: string;
	shouldExport?: boolean;
};

export function addAction(
	entry: AddPayload,
	deps: ActionDeps,
	onSuccess?: () => void,
	onEntryFailure?: (id: number, error: string) => void,
): QueuedAction {
	const { shouldExport, ...body } = entry;
	return createAction({
		execute: async () => {
			const resp = await deps.makeApiRequest({
				endpoint: "/records/add",
				method: "POST",
				body,
			});
			if (resp.status === "ERROR") throw new Error(resp.error || "Request failed");
			if (shouldExport) {
				if (!resp.data?.id) throw Object.assign(new Error("No entry ID returned"), { phase: "export" });
				const entryId = resp.data.id;
				const exportResp = await deps.makeApiRequest({
					endpoint: "/records/exportRecords",
					method: "POST",
					body: { ids: [entryId] },
				});
				if (exportResp.status === "ERROR") {
					const msg = exportResp.error || "Export failed";
					onEntryFailure?.(entryId, msg);
					throw Object.assign(new Error(msg), { phase: "export" });
				}
			}
		},
		rollback: null,
		toast: {
			loading: "Adding entry\u2026",
			success: shouldExport ? "Entry added and exported" : "Entry added",
			error: shouldExport
				? (err: unknown) => (err as any)?.phase === "export" ? "Entry added but export failed" : "Failed to add entry"
				: "Failed to add entry",
		},
		onSuccess: () => { deps.refreshRecords(); onSuccess?.(); },
		onError: () => deps.refreshRecords(),
	});
}
